/**
 * Typed React Hooks for eStream Console Data
 *
 * These hooks provide type-safe access to common lattice topics
 * using the generated ESF types.
 */
import { useSubscription, useEmit, useEstreamClient } from './react.js';
// ============================================================================
// Topology Hooks
// ============================================================================
/**
 * Subscribe to the full topology snapshot
 *
 * @example
 * ```tsx
 * function NetworkMap() {
 *   const { data: topology, loading, error } = useTopology();
 *
 *   if (loading) return <Spinner />;
 *   if (error) return <Error message={error.message} />;
 *
 *   return <D3Graph nodes={topology.nodes} links={topology.links} />;
 * }
 * ```
 */
export function useTopology() {
    return useSubscription('/console/topology/snapshot');
}
/**
 * Subscribe to topology changes (deltas)
 * More efficient than full snapshots for real-time updates
 */
export function useTopologyDelta() {
    return useSubscription('/console/topology/delta');
}
/**
 * Subscribe to a specific node's updates
 */
export function useNode(nodeId) {
    return useSubscription(`/console/topology/nodes/${nodeId}`);
}
// ============================================================================
// Metrics Hooks
// ============================================================================
/**
 * Subscribe to network-wide metrics
 *
 * @example
 * ```tsx
 * function MetricsPanel() {
 *   const { data: metrics, loading } = useNetworkMetrics();
 *
 *   if (loading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       <Stat label="TPS" value={metrics.tps} />
 *       <Stat label="Latency" value={`${metrics.latencyP50}ms`} />
 *       <Stat label="Nodes" value={metrics.activeNodes} />
 *     </div>
 *   );
 * }
 * ```
 */
export function useNetworkMetrics() {
    return useSubscription('/console/metrics/network');
}
/**
 * Subscribe to full metrics snapshot
 */
export function useMetricsSnapshot() {
    return useSubscription('/console/metrics/snapshot');
}
/**
 * Subscribe to a specific node's metrics
 */
export function useNodeMetrics(nodeId) {
    return useSubscription(`/metrics/node/${nodeId}`);
}
/**
 * Subscribe to circuits state (pending, completed, deployed)
 *
 * @example
 * ```tsx
 * function CircuitsPage() {
 *   const { data: circuits, loading } = useCircuits();
 *
 *   if (loading) return <Spinner />;
 *
 *   return (
 *     <Tabs>
 *       <Tab label={`Pending (${circuits.pending.length})`}>
 *         <CircuitList circuits={circuits.pending} />
 *       </Tab>
 *       <Tab label={`Deployed (${circuits.deployed.length})`}>
 *         <DeployedList circuits={circuits.deployed} />
 *       </Tab>
 *     </Tabs>
 *   );
 * }
 * ```
 */
export function useCircuits() {
    return useSubscription('/console/circuits/state');
}
/**
 * Subscribe to pending circuit proposals
 */
export function usePendingCircuits() {
    return useSubscription('/console/circuits/pending');
}
/**
 * Submit a circuit proposal
 */
export function useSubmitCircuit() {
    return useEmit('/circuits/submit');
}
/**
 * Subscribe to governance state
 *
 * @example
 * ```tsx
 * function GovernancePage() {
 *   const { data: gov, loading } = useGovernance();
 *   const { emit: vote } = useVote();
 *
 *   const handleApprove = async (proposalId: string) => {
 *     await vote({ proposalId, approve: true });
 *   };
 *
 *   return (
 *     <ProposalList
 *       proposals={gov.pending}
 *       onApprove={handleApprove}
 *     />
 *   );
 * }
 * ```
 */
export function useGovernance() {
    return useSubscription('/console/governance/state');
}
/**
 * Submit a vote on a governance proposal
 */
export function useVote() {
    return useEmit('/governance/vote');
}
/**
 * Get detailed connection information
 *
 * @example
 * ```tsx
 * function ConnectionStatus() {
 *   const info = useConnectionInfo();
 *
 *   return (
 *     <div className={info.isConnected ? 'status-ok' : 'status-error'}>
 *       <span>{info.state}</span>
 *       {info.latencyMs && <span>{info.latencyMs}ms</span>}
 *     </div>
 *   );
 * }
 * ```
 */
export function useConnectionInfo() {
    const client = useEstreamClient();
    return {
        state: client.state,
        isConnected: client.isConnected,
        network: client.network,
        edgeUrl: '', // TODO: expose from client
        latencyMs: undefined, // TODO: implement ping
    };
}
//# sourceMappingURL=hooks.js.map